// Top-level router: landing -> community picker -> BoardIQ app shell.
// Hash-based (no dependency) so it works from a static file server as-is.
import React, { useCallback, useEffect, useState } from "react";
import Landing from "./Landing.jsx";
import CommunityPicker from "./CommunityPicker.jsx";
import BoardIQ from "./BoardIQ.jsx";
import AdminApp from "./AdminApp.jsx";
import { useTheme } from "./theme.js";
import { SessionProvider, useSession } from "./session.js";
import "./tokens.css";

const routeFromHash = () => {
  const h = location.hash.replace(/^#\/?/, "");
  if (h === "communities" || h === "app") return h;
  // "admin" also covers sub-paths like admin/communities/{id} -- AdminApp
  // itself parses those further (see its own parseDetailId), but this
  // top-level router has to recognize the prefix first or it never mounts
  // AdminApp at all and falls through to the landing page instead.
  if (h === "admin" || h.startsWith("admin/")) return "admin";
  return "landing";
};

export default function App() {
  return (
    <SessionProvider>
      <AppRouter />
    </SessionProvider>
  );
}

// Split out so it can call useSession() -- which requires a SessionProvider
// ancestor -- while App() itself stays the thing that provides one.
function AppRouter() {
  const [route, setRoute] = useState(routeFromHash);
  const view = new URLSearchParams(location.search).get("view") || "auto";
  const { startSession } = useSession();

  // Applies data-theme/data-font on <html> as soon as the app mounts, so
  // Landing and CommunityPicker -- which render before BoardIQ.jsx gets a
  // chance to call this itself -- are never left unstyled. Defaults to
  // Modernist (theme id "light") when nothing is persisted yet; BoardIQ.jsx
  // calls the same hook again once mounted, which just re-syncs the same
  // persisted/broadcast value rather than fighting this one.
  useTheme("light");

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const go = useCallback((next) => {
    location.hash = next === "landing" ? "/" : `/${next}`;
    setRoute(next);
  }, []);

  // Populates the in-memory session (community, signed-in user, login time)
  // the moment CommunityPicker's login succeeds -- before BoardIQ.jsx ever
  // mounts, so its first render already has a real community/user instead
  // of the "No community" placeholder while GET /community is in flight.
  const onSelectCommunity = useCallback((sel) => {
    startSession({
      communityId: sel.communityId,
      communityName: sel.name,
      userName: sel.username,
      role: sel.role,
      resident: sel.resident,
    });
    go("app");
  }, [startSession, go]);

  if (route === "admin") return <AdminApp />;
  if (route === "app") return <BoardIQ view={view} />;
  if (route === "communities") {
    return <CommunityPicker onBack={() => go("landing")} onSelectCommunity={onSelectCommunity} />;
  }
  return <Landing onEnterApp={() => go("app")} onEnterPicker={() => go("communities")} />;
}
